/** Tasks & material requests unlock once at least one installation member is on the project team. */
export const hasProjectInstallationTeam = (project) =>
  Array.isArray(project?.fieldOfficers) && project.fieldOfficers.length > 0;

const receiptUserIdSet = (project) => {
  const ids = new Set();
  for (const r of project?.materialReceipts || []) {
    const id = r?.confirmedBy?._id ?? r?.confirmedBy;
    if (id != null) ids.add(String(id));
  }
  return ids;
};

/** Installation members on this project who confirmed material receipt — only these may receive assigned tasks. */
export const fieldOfficersEligibleForTasks = (project, requests = []) => {
  const allowed = receiptUserIdSet(project);
  
  // Also include users who have received at least one item on site
  (requests || []).forEach(req => {
    if ((req.items || []).some(it => it.siteReceivedAt)) {
      const uid = req.assignedTo?._id || req.assignedTo || (typeof req.assignedTo === 'string' ? req.assignedTo : null);
      if (uid) allowed.add(String(uid));
    }
  });

  if (allowed.size === 0) return [];
  return (project?.fieldOfficers || []).filter((o) => {
    const id = typeof o === 'object' && o?._id != null ? String(o._id) : String(o);
    return allowed.has(id);
  });
};

export const hasMaterialReceiptForTaskAssignment = (project, requests = []) =>
  fieldOfficersEligibleForTasks(project, requests).length > 0;

/** Whether the given user confirmed receipt on this project (for field officer gate UI). */
export const hasUserConfirmedMaterialReceipt = (project, userId) => {
  if (userId == null) return false;
  const uid = String(userId);
  return (project?.materialReceipts || []).some((r) => {
    const id = r?.confirmedBy?._id ?? r?.confirmedBy;
    return id != null && String(id) === uid;
  });
};

/**
 * Installation member: unlock tasks/inventory when legacy receipt exists OR every dispatched line they must receive is marked received.
 * `requests` should be project material requests for this user (API already filters by assignee for field officers).
 */
export const getInstallationMemberReceiptGate = (project, userId, requests = []) => {
  if (userId != null && hasUserConfirmedMaterialReceipt(project, userId)) {
    return { complete: true, receivableCount: 0, pendingCount: 0, awaitingDispatch: false };
  }
  const dispatched = (requests || []).filter((r) => ['DISPATCHED', 'DELIVERED'].includes(r.status));
  let receivableCount = 0;
  let pendingCount = 0;
  for (const r of dispatched) {
    for (const it of r.items || []) {
      const issued = Number(it.issuedQuantity || 0);
      if (issued <= 0) continue;
      receivableCount += 1;
      if (!it.siteReceivedAt) pendingCount += 1;
    }
  }
  const complete = receivableCount > 0 && pendingCount === 0;
  /** No issued/dispatched lines for this member yet — do not show legacy bulk confirm. */
  const awaitingDispatch = receivableCount === 0;
  return { complete, receivableCount, pendingCount, awaitingDispatch };
};
